import { Message } from "@faire/slate/foundation/color";
import { useStrictLocalization } from "@faire/web/common/localization";
import { Input } from "@faire/web/slate-deprecated/Input";
import { Column, Row } from "@faire/web/slate/Layout";
import { getSpacing, Spacer } from "@faire/web/slate/spacing";
import { Typography } from "@faire/web/slate/Typography";
import { TypographyStyles } from "@faire/web/slate/Typography";
import React, {
  createRef,
  useCallback,
  useMemo,
  useState,
  type JSX,
} from "react";
import styled from "styled-components";

interface VerificationInputProps {
  value: string;
  /**
   * Aria Label, for accessibility as there are no labels
   */
  ariaLabel?: string;
  onChange?: (code: string, value: string) => void;
  /**
   * Validation function for each individual input field
   */
  validateFn?: (value: string) => boolean;
  /**
   * Determines number of input slots to render
   */
  numSlots?: number;
  /**
   * Error message shown below the input wrapper
   */
  errorMessage?: JSX.Element | string;
  /**
   * Sets all input fields to error state
   */
  errorAll?: boolean;
  /**
   * Determines valid input has been entered into the component
   */
  isValidInput?: boolean;
  maxWidth?: string;
  /**
   * Disables auto focus on first input field when component is loaded
   */
  disableAutoFocus?: boolean;
  /**
   * Label displayed above the first input field
   */
  label?: string;
  /**
   * Clears the entire input field
   */
  clearInput?: boolean;
}

const DEFAULT_VALUE_LENGTH = 6;

export const VerificationInput: React.FC<VerificationInputProps> = ({
  value,
  numSlots,
  errorMessage,
  errorAll,
  isValidInput,
  validateFn,
  onChange,
  maxWidth,
  disableAutoFocus,
  label,
  ariaLabel,
  clearInput,
}) => {
  const { strictLocalize } = useStrictLocalization();
  const numInputSlots = numSlots ?? DEFAULT_VALUE_LENGTH;
  const cleanedValue = [
    ...value.split(""),
    ...Array(numInputSlots).fill(""),
  ].slice(0, numInputSlots);

  const [code, setCode] = useState(cleanedValue);

  const slotInputRefs = useMemo(
    () =>
      Array(numInputSlots)
        .fill(null)
        .map(() => createRef<HTMLInputElement>()),
    [numInputSlots]
  );

  const processInput = (
    e: React.ChangeEvent<HTMLInputElement>,
    inputIndex: number
  ) => {
    const inputValue = e.target.value;
    if (inputValue.length > 1) {
      return;
    }

    const newCode = [...code];
    newCode[inputIndex] = inputValue;
    setCode(newCode);
    onChange?.(newCode.join(""), inputValue);
    if (inputValue && inputIndex !== numInputSlots - 1) {
      focusSlot(inputIndex + 1);
    }
  };

  const handleOnPaste = (
    e: React.ClipboardEvent<HTMLInputElement>,
    slotIndex: number
  ) => {
    const clipboardData = e.clipboardData.getData("text").split("");
    if (slotIndex !== 0 || !clipboardData.length) {
      return;
    }

    const newCode = [...clipboardData, ...Array(numInputSlots).fill("")].slice(
      0,
      numInputSlots
    );

    setCode(newCode);
    const mergedCode = newCode.join("");
    onChange?.(mergedCode, clipboardData.join(""));
  };

  const focusSlot = (index: number) => {
    slotInputRefs[index]?.current?.focus();
  };

  const blurSlot = useCallback(
    (index: number) => {
      slotInputRefs[index]?.current?.blur();
    },
    [slotInputRefs]
  );

  const handleBackspace = (index: number) => (e: React.KeyboardEvent) => {
    const keyCode = e.keyCode;

    if (keyCode === 8 && !code[index]) {
      e.preventDefault();
      const newCode = [...code];
      newCode[index - 1] = "";
      setCode(newCode);
      onChange?.(newCode.join(""), "");
      focusSlot(index - 1);
    }
  };

  // Clears the input field if needed
  React.useEffect(() => {
    if (clearInput && value === "") {
      setCode(Array(numInputSlots).fill(""));
      blurSlot(numInputSlots - 1);
    }
  }, [clearInput, numInputSlots, blurSlot, value]);

  // Unfocuses all the input fields when a valid combined input is given
  React.useEffect(() => {
    if (isValidInput) {
      slotInputRefs.map((slotInput) => slotInput.current?.blur());
    }
  }, [isValidInput, slotInputRefs]);

  return (
    <Column>
      {label ? (
        <>
          <Typography>{label}</Typography>
          <Spacer height={4} />
        </>
      ) : null}
      <InputWrapper $maxWidth={maxWidth}>
        {code.map((slotValue: string, index: number) => {
          return (
            <SlotInput
              type="numeric"
              inputMode="numeric"
              pattern="[0-9]*"
              key={index}
              autoFocus={!disableAutoFocus && !code?.[0]?.length && index === 0}
              value={slotValue}
              error={!validateFn?.(slotValue) || errorAll === true}
              onChange={(e) => processInput(e, index)}
              onPaste={(e) => handleOnPaste(e, index)}
              onClick={() => focusSlot(index)}
              onKeyDown={handleBackspace(index)}
              aria-label={
                ariaLabel
                  ? `${ariaLabel} ${index}`
                  : strictLocalize(
                      {
                        id: "DoNYuH",
                        defaultMessage: "Enter Slot Value {indexNumber}",
                        description: {
                          text: "Accessible name for user to enter slot value",
                        },
                      },
                      {
                        indexNumber: index,
                      }
                    )
              }
              data-test-id={`input-slot-${index}`}
              ref={slotInputRefs[index]}
            />
          );
        })}
      </InputWrapper>
      <Spacer height="1x" />
      {errorMessage ? (
        <Typography color={Message.text.critical}>{errorMessage}</Typography>
      ) : null}
    </Column>
  );
};

const InputWrapper = styled(Row).attrs({ gap: "2x" })<{ $maxWidth?: string }>`
  width: 100%;
  max-width: ${(props) => props.$maxWidth ?? "340px"};
`;

const SlotInput = styled(Input)`
  height: 48px;
  padding: 11px ${getSpacing("1x")};

  input {
    ${TypographyStyles({
      variant: "subheadingSansRegular",
    })}
    text-align: center;
  }
`;
